import { useMemo, type ReactNode } from 'react'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { Button } from '@/components/ui/Button'

type StudioLike = {
  studio_internal_id: number
  studio_number: string | number
  city_id?: number
  city?: { city_name?: string }
  city_name?: string
  address?: string | null
}

interface StudioPickerSheetProps {
  isOpen: boolean
  onClose: () => void
  title: string
  studios: StudioLike[]
  selectedIds: number[]
  onChange: (ids: number[]) => void
  /** 'specific' = pick individual studios, 'all' = all studios (incl. future) */
  accessScope?: 'specific' | 'all'
  onAccessScopeChange?: (scope: 'specific' | 'all') => void
  confirmLabel?: string
  onConfirm?: () => void
  saving?: boolean
  headerContent?: ReactNode
  showStudioSelection?: boolean
}

export function StudioPickerSheet({
  isOpen,
  onClose,
  title,
  studios,
  selectedIds,
  onChange,
  accessScope = 'specific',
  onAccessScopeChange,
  confirmLabel = 'Готово',
  onConfirm,
  saving = false,
  headerContent,
  showStudioSelection = true,
}: StudioPickerSheetProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, StudioLike[]>()
    for (const s of studios) {
      const city = s.city_name || s.city?.city_name || 'Город не указан'
      if (!map.has(city)) map.set(city, [])
      map.get(city)!.push(s)
    }
    return Array.from(map.entries()).sort(([a], [b]) =>
      a.localeCompare(b, 'ru', { sensitivity: 'base' })
    )
  }, [studios])

  const toggle = (id: number) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id]
    )
  }

  const toggleCity = (cityStudios: StudioLike[]) => {
    const cityIds = cityStudios.map(s => s.studio_internal_id)
    const allChecked = cityIds.every(id => selectedIds.includes(id))
    onChange(
      allChecked
        ? selectedIds.filter(id => !cityIds.includes(id))
        : [...selectedIds, ...cityIds.filter(id => !selectedIds.includes(id))]
    )
  }

  const selectAll = () => onChange(studios.map(s => s.studio_internal_id))
  const clearAll  = () => onChange([])

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={title}>
      <div className="px-4 pt-3 pb-6 space-y-4">
        {headerContent}

        {/* access_scope toggle */}
        {showStudioSelection && onAccessScopeChange && (
          <button
            onClick={() => onAccessScopeChange(accessScope === 'all' ? 'specific' : 'all')}
            className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg text-left"
          >
            <div>
              <p className="text-sm font-medium">Все студии (включая будущие)</p>
              <p className="text-xs text-muted-foreground">
                Доступ обновляется автоматически при добавлении новых студий
              </p>
            </div>
            <div
              className={`w-11 h-6 rounded-full transition-colors flex-shrink-0 ml-3 ${
                accessScope === 'all' ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
            >
              {/* Ползунок переключателя: белый кружок лежит на сплошной цветной
                  дорожке (`bg-primary` / `bg-muted-foreground/30`), поэтому белый
                  здесь обязателен по смыслу и остаётся литералом — `bg-card` в
                  тёмной теме слил бы кружок с дорожкой. */}
              <div
                className={`w-5 h-5 bg-white rounded-full shadow mt-0.5 transition-transform ${
                  accessScope === 'all' ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </div>
          </button>
        )}

        {/* Studio list — only for 'specific' scope */}
        {showStudioSelection && accessScope !== 'all' && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Выбрано: {selectedIds.length} / {studios.length}
              </span>
              <div className="flex gap-3">
                <button onClick={selectAll} className="text-xs text-primary hover:underline">
                  Все
                </button>
                <button onClick={clearAll} className="text-xs text-muted-foreground hover:underline">
                  Сбросить
                </button>
              </div>
            </div>

            {grouped.map(([cityName, cityStudios]) => {
              const cityIds      = cityStudios.map(s => s.studio_internal_id)
              const checkedCount = cityIds.filter(id => selectedIds.includes(id)).length
              const allChecked   = checkedCount === cityIds.length
              const someChecked  = checkedCount > 0 && !allChecked

              return (
                <div key={cityName}>
                  {/* City row — click to toggle whole city */}
                  <button
                    onClick={() => toggleCity(cityStudios)}
                    className="flex items-center gap-2 w-full py-2 text-left"
                  >
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        allChecked
                          ? 'bg-primary border-primary'
                          : someChecked
                          ? 'border-primary'
                          : 'border-border'
                      }`}
                    >
                      {allChecked && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 8">
                          <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                      {someChecked && (
                        <div className="w-2 h-0.5 bg-primary rounded" />
                      )}
                    </div>
                    <span className="font-medium text-sm">{cityName}</span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      {checkedCount}/{cityIds.length}
                    </span>
                  </button>

                  {/* Studios */}
                  <div className="space-y-1.5 pl-6">
                    {cityStudios.map(studio => {
                      const checked = selectedIds.includes(studio.studio_internal_id)
                      const address = (studio.address ?? '').trim()
                      return (
                        <label
                          key={studio.studio_internal_id}
                          className={`flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer transition-colors ${
                            checked
                              ? 'border-primary bg-primary/5'
                              : 'border-border bg-background'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(studio.studio_internal_id)}
                            className="h-4 w-4 rounded border-border flex-shrink-0"
                          />
                          <span className="text-sm min-w-0">
                            №{studio.studio_number}
                            {address && (
                              <span className="text-muted-foreground"> • {address}</span>
                            )}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </>
        )}

        {showStudioSelection && accessScope === 'all' && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Сотрудник автоматически видит все {studios.length} студий,
            включая те, что появятся позже
          </p>
        )}

        {onConfirm && (
          <Button
            className="w-full"
            onClick={onConfirm}
            disabled={saving || (showStudioSelection && accessScope === 'specific' && selectedIds.length === 0)}
          >
            {saving ? 'Сохранение...' : confirmLabel}
          </Button>
        )}
      </div>
    </BottomSheet>
  )
}
